/**
 * Resource schemas — declaratively drive the admin CRUD forms.
 * Each field:  { key, label, type, placeholder?, options?, help? }
 * type: text | textarea | number | select | tags | color | url
 */

export const RESOURCES = [
  {
    key: "credentials",
    label: "Credentials",
    description: "Certificates, programmes, courses — the paper trail.",
    order: 1,
    fields: [
      { key: "title", label: "Title", type: "text", placeholder: "Young Leaders Program 2026" },
      { key: "issuer", label: "Issuer", type: "text", placeholder: "36ONE Asset Management" },
      { key: "issued", label: "Issued", type: "text", placeholder: "Jul 2025" },
      { key: "credentialId", label: "Credential ID", type: "text", placeholder: "N7WQXT4ZTKBP" },
      { key: "category", label: "Category", type: "select", options: ["Capital", "Build", "Leadership", "Research", "Academic", "Frontier"] },
      { key: "skills", label: "Skills", type: "tags", placeholder: "Add skill and press Enter" },
      { key: "detail", label: "Detail / Description", type: "textarea" },
      { key: "certificate", label: "Certificate (image or PDF)", type: "file", help: "Attach the scanned certificate — image or PDF, up to 25MB." },
      { key: "accent", label: "Accent colour", type: "color" },
    ],
    card: (item) => ({ title: item.title, subtitle: item.issuer, meta: item.issued }),
    defaults: { category: "Capital", accent: "#e6c769", skills: [] },
  },
  {
    key: "awards",
    label: "Awards",
    description: "Trophies, distinctions, and moments that meant something.",
    order: 2,
    fields: [
      { key: "title", label: "Title", type: "text", placeholder: "Pickleball & Junoon Season 1" },
      { key: "subtitle", label: "Subtitle", type: "text", placeholder: "Tournament Winner" },
      { key: "org", label: "Organisation", type: "text" },
      { key: "year", label: "Year", type: "text", placeholder: "2025" },
      { key: "date", label: "Date detail", type: "text" },
      { key: "category", label: "Category", type: "select", options: ["Sport", "Academic", "Research", "Leadership"] },
      { key: "image", label: "Photo / trophy image", type: "image", help: "Upload the moment — JPEG, PNG, or WebP up to 25MB." },
      { key: "detail", label: "Detail / Description", type: "textarea" },
      { key: "accent", label: "Accent colour", type: "color" },
    ],
    card: (item) => ({ title: item.title, subtitle: item.subtitle, meta: item.year }),
    defaults: { category: "Sport", accent: "#e6c769" },
  },
  {
    key: "experiences",
    label: "Experience",
    description: "Companies and roles.",
    order: 3,
    fields: [
      { key: "company", label: "Company", type: "text", placeholder: "FYSE Ventures SRL" },
      { key: "role", label: "Role", type: "text", placeholder: "Distributor · Incoming Director" },
      { key: "span", label: "Span", type: "text", placeholder: "2026 — Present" },
      { key: "location", label: "Location", type: "text", placeholder: "Italy · International" },
      { key: "website", label: "Website URL", type: "url" },
      { key: "websiteDisplay", label: "Website display", type: "text", placeholder: "fyseitaly.com" },
      { key: "current", label: "Current role?", type: "bool" },
      { key: "summary", label: "Summary", type: "textarea" },
      { key: "skills", label: "Skills earned", type: "tags" },
      { key: "achievements", label: "What I shipped", type: "tags" },
    ],
    card: (item) => ({ title: item.company, subtitle: item.role, meta: item.span }),
    defaults: { current: false, skills: [], achievements: [] },
  },
  {
    key: "projects",
    label: "Projects",
    description: "Built, shipped, live in production.",
    order: 4,
    fields: [
      { key: "name", label: "Project name", type: "text" },
      { key: "tagline", label: "Tagline", type: "text" },
      { key: "url", label: "Live URL", type: "url", placeholder: "https://…" },
      { key: "displayUrl", label: "Display URL", type: "text", placeholder: "example.com" },
      { key: "year", label: "Year", type: "text" },
      { key: "role", label: "Role", type: "text", placeholder: "Designer · Developer · Project Lead" },
      { key: "mission", label: "Mission / Description", type: "textarea" },
      { key: "impact", label: "Impact points", type: "tags" },
      { key: "stack", label: "Tech stack", type: "tags" },
      { key: "preview", label: "Preview image (upload or URL)", type: "image", help: "Uploads override any URL." },
      { key: "accent", label: "Accent colour", type: "color" },
    ],
    card: (item) => ({ title: item.name, subtitle: item.tagline, meta: item.year }),
    defaults: { impact: [], stack: [], accent: "#e6c769" },
  },
  {
    key: "skills",
    label: "Skills",
    description: "Toolkit — grouped by intent.",
    order: 5,
    fields: [
      { key: "name", label: "Skill name", type: "text" },
      { key: "group", label: "Group", type: "select", options: ["Build", "Capital", "Analyse", "Frontier", "People"] },
      { key: "note", label: "Note / credential", type: "textarea" },
    ],
    card: (item) => ({ title: item.name, subtitle: item.group, meta: "" }),
    defaults: { group: "Build" },
  },
  {
    key: "places",
    label: "Places",
    description: "Cities on the map — India and Cambodia.",
    order: 6,
    fields: [
      { key: "name", label: "Name", type: "text" },
      { key: "country", label: "Country", type: "select", options: ["India", "India · Delhi NCR", "Cambodia"] },
      { key: "phase", label: "Phase", type: "text" },
      { key: "year", label: "Year(s)", type: "text" },
      { key: "role", label: "Role / theme", type: "text" },
      { key: "lat", label: "Latitude", type: "number", placeholder: "23.03" },
      { key: "lng", label: "Longitude", type: "number", placeholder: "72.58" },
      { key: "note", label: "Note", type: "textarea" },
    ],
    card: (item) => ({ title: item.name, subtitle: item.country, meta: item.year }),
    defaults: { country: "India" },
  },
  {
    key: "images",
    label: "Images",
    description: "Photo library — upload new images for your site.",
    order: 7,
    fields: [
      { key: "title", label: "Title", type: "text", placeholder: "Siem Reap · Village school" },
      { key: "url", label: "Image (upload)", type: "image" },
      { key: "category", label: "Category", type: "select", options: ["Story", "Moment", "Award", "Experience", "Project", "Other"] },
      { key: "caption", label: "Caption / Description", type: "textarea" },
      { key: "date", label: "Date taken", type: "text", placeholder: "Jun 2024" },
      { key: "location", label: "Location", type: "text", placeholder: "Siem Reap, Cambodia" },
    ],
    card: (item) => ({ title: item.title, subtitle: item.caption, meta: item.date }),
    defaults: { category: "Moment" },
  },
];

export const findResource = (key) => RESOURCES.find((r) => r.key === key);
